'use client';
import React, { useState } from 'react';
import BookingProgressBar from './BookingProgressBar';
import BookingSummaryPanel from './BookingSummaryPanel';
import StepServiceSelection from './StepServiceSelection';
import StepDateTime from './StepDateTime';
import StepYourDetails from './StepYourDetails';
import StepPayment from './StepPayment';
import BookingConfirmation from './BookingConfirmation';
import { sendBookingConfirmation } from '@/lib/emailService';

export interface SelectedService {
  id: string;
  name: string;
  price: number;
  priceLabel: string;
  vehicleSize: string;
  duration: number;
  addons: { id: string; name: string; price: number; priceLabel: string }[];
}

export interface BookingDateTime {
  date: string;
  time: string;
}

export interface ClientDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vehicleColor: string;
  notes: string;
  createAccount: boolean;
}

export interface BookingState {
  service: SelectedService | null;
  dateTime: BookingDateTime | null;
  clientDetails: ClientDetails | null;
  paymentType: 'deposit' | 'full';
  bookingId: string | null;
}

const STEPS = ['Service', 'Date & Time', 'Your Details', 'Payment'];

export default function BookingWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [booking, setBooking] = useState<BookingState>({
    service: null,
    dateTime: null,
    clientDetails: null,
    paymentType: 'deposit',
    bookingId: null,
  });
  const [confirmed, setConfirmed] = useState(false);

  const handleServiceSelect = (service: SelectedService) => {
    setBooking((b) => ({ ...b, service }));
    setCurrentStep(1);
  };

  const handleDateTimeSelect = (dateTime: BookingDateTime) => {
    setBooking((b) => ({ ...b, dateTime }));
    setCurrentStep(2);
  };

  const handleDetailsSubmit = (clientDetails: ClientDetails) => {
    setBooking((b) => ({ ...b, clientDetails }));
    setCurrentStep(3);
  };

  const handlePaymentComplete = async (paymentType: 'deposit' | 'full', bookingId: string) => {
    const updatedBooking = { ...booking, paymentType, bookingId };
    setBooking(updatedBooking);
    setConfirmed(true);

    // Send booking confirmation email via Resend
    if (updatedBooking.clientDetails && updatedBooking.service && updatedBooking.dateTime) {
      const { service, dateTime, clientDetails } = updatedBooking;
      const addonsTotal = service.addons.reduce((sum, a) => sum + a.price, 0);
      const subtotal = service.price + addonsTotal;
      const depositPaid = paymentType === 'deposit' ? Math.round(subtotal * 0.3) : subtotal;

      await sendBookingConfirmation({
        clientName: `${clientDetails.firstName} ${clientDetails.lastName}`,
        clientEmail: clientDetails.email,
        bookingId,
        service: service.name,
        vehicleSize: service.vehicleSize,
        vehicle: `${clientDetails.vehicleYear} ${clientDetails.vehicleMake} ${clientDetails.vehicleModel}`,
        date: dateTime.date,
        time: dateTime.time,
        addons: service.addons.map((a) => ({ name: a.name, price: a.price })),
        subtotal,
        depositPaid,
        paymentType,
      });
    }
  };

  if (confirmed && booking.bookingId) {
    return <BookingConfirmation booking={booking} />;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col">
      {/* Progress bar */}
      <BookingProgressBar steps={STEPS} currentStep={currentStep} />

      <div className="flex-1 flex max-w-screen-2xl mx-auto w-full px-6 lg:px-8 xl:px-10 2xl:px-16 py-8 gap-8">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {currentStep === 0 && (
            <StepServiceSelection onSelect={handleServiceSelect} selectedId={booking.service?.id ?? null} />
          )}
          {currentStep === 1 && (
            <StepDateTime
              onSelect={handleDateTimeSelect}
              onBack={() => setCurrentStep(0)}
              selected={booking.dateTime}
              serviceDurationHours={booking.service?.duration ?? 1}
            />
          )}
          {currentStep === 2 && (
            <StepYourDetails
              onSubmit={handleDetailsSubmit}
              onBack={() => setCurrentStep(1)}
              initial={booking.clientDetails}
            />
          )}
          {currentStep === 3 && booking.service && booking.dateTime && booking.clientDetails && (
            <StepPayment
              booking={booking}
              onComplete={handlePaymentComplete}
              onBack={() => setCurrentStep(2)}
              onPaymentTypeChange={(t) => setBooking((b) => ({ ...b, paymentType: t }))}
            />
          )}
        </div>

        {/* Summary panel — visible from step 1 onward */}
        {currentStep > 0 && (
          <div className="hidden lg:block w-80 xl:w-96 flex-shrink-0">
            <BookingSummaryPanel booking={booking} currentStep={currentStep} />
          </div>
        )}
      </div>
    </div>
  );
}